import { Component, OnDestroy, OnInit } from '@angular/core';
import { Button, ButtonColor, ButtonSize } from 'app/store/models/button.model';
import {
  IUserApiKey,
  IUserApiKeyCreated,
  MY_SPACE_SCOUT_API_KEY_NAME,
} from 'app/store/models/api-key.model';
import { IUserSessionData, UserType } from 'app/store/models/user.model';
import { SessionService } from 'app/store/services/session.service';
import { UserService } from 'app/store/services/user.service';
import { DialogComponent } from 'app/shared/ui/dialog/dialog.component';
import { DialogService } from 'app/shared/ui/dialog/dialog.service';
import { SnackbarService } from 'app/shared/ui/snackbar/snackbar.service';
import { getApiErrorMessage } from 'app/shared/http/api-error-message';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-api-keys',
  templateUrl: './api-keys.component.html',
  styleUrls: ['./api-keys.component.scss'],
})
export class ApiKeysComponent implements OnInit, OnDestroy {
  readonly mySpaceScoutKeyName = MY_SPACE_SCOUT_API_KEY_NAME;

  sessionData: IUserSessionData;
  apiKeys: IUserApiKey[] = [];
  loading = false;

  cancelButtonProperties: Button = {
    text: 'Back to Inbox',
    color: ButtonColor.BLUE,
    size: ButtonSize.SMALL,
    route: ['/messages'],
    style: 'outline',
  };

  createButtonProperties: Button = {
    text: 'Create My Space Scout Key',
    color: ButtonColor.ORANGE,
    size: ButtonSize.SMALL,
    style: 'rounded',
    onClick: () => this.createApiKey(),
  };

  private revokedKeyIds = new Set<number>();
  private destroy$ = new Subject<boolean>();

  constructor(
    private userService: UserService,
    private sessionService: SessionService,
    private dialogService: DialogService,
    private snackbarService: SnackbarService,
  ) {}

  get canCreateApiKeys(): boolean {
    if (!this.sessionData?.api_keys_feature_enabled) {
      return false;
    }
    if (this.sessionData.user_type === UserType.MASTER) {
      return true;
    }
    return !!this.sessionData.api_keys_enabled;
  }

  get hasMySpaceScoutKey(): boolean {
    return this.apiKeys.some(
      key => key.name === this.mySpaceScoutKeyName && !this.revokedKeyIds.has(key.id),
    );
  }

  get showCreateMySpaceScoutKey(): boolean {
    return this.canCreateApiKeys && !this.hasMySpaceScoutKey;
  }

  ngOnInit(): void {
    this.sessionService
      .onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe(session => {
        this.sessionData = session;
        if (!session?.api_keys_feature_enabled) {
          return;
        }
        this.loadApiKeys();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  loadApiKeys(): void {
    this.loading = true;
    this.userService.listApiKeys().subscribe(
      keys => {
        this.apiKeys = keys || [];
        this.loading = false;
      },
      error => {
        this.loading = false;
        this.showError(error, 'Failed to load API keys.');
      },
    );
  }

  createApiKey(): void {
    if (!this.showCreateMySpaceScoutKey) {
      return;
    }

    this.createButtonProperties.observeApi = true;
    this.userService.createApiKey(this.mySpaceScoutKeyName).subscribe(
      created => {
        this.createButtonProperties.observeApi = false;
        this.loadApiKeys();
        this.showCreatedKeyDialog(created);
      },
      error => {
        this.createButtonProperties.observeApi = false;
        this.showError(error, 'Failed to create API key.');
      },
    );
  }

  revokeApiKey(key: IUserApiKey): void {
    this.dialogService.openDialog({
      title: 'Revoke API Key',
      content: `Revoke "${key.name}" (${key.key_prefix})? This cannot be undone.`,
      canClose: false,
      buttons: [
        {
          text: 'Cancel',
          color: ButtonColor.RED,
          size: ButtonSize.SMALL,
          actAsClose: true,
          isCloseButton: true,
        },
        {
          text: 'Revoke',
          color: ButtonColor.ORANGE,
          size: ButtonSize.SMALL,
          onClick: (dialog: DialogComponent) => {
            this.userService.revokeApiKey(key.id).subscribe(
              () => {
                if (key.name === this.mySpaceScoutKeyName) {
                  this.revokedKeyIds.add(key.id);
                }
                this.showSuccess('API key revoked.');
                this.loadApiKeys();
                dialog.closeDialog();
              },
              error => {
                this.showError(error, 'Failed to revoke API key.');
                dialog.releaseButtons();
              },
            );
          },
        },
      ],
    });
  }

  onRevokeClick(key: IUserApiKey): void {
    this.revokeApiKey(key);
  }

  copyToClipboard(value: string, dialog?: DialogComponent): void {
    const onCopied = (): void => {
      this.showSuccess('Copied to clipboard.', 3000);
      dialog?.releaseButtons();
    };
    const onCopyFailed = (): void => {
      this.showError(null, 'Failed to copy to clipboard.');
      dialog?.releaseButtons();
    };

    if (navigator?.clipboard?.writeText && window.isSecureContext) {
      navigator.clipboard.writeText(value).then(onCopied, onCopyFailed);
      return;
    }

    if (this.copyToClipboardFallback(value)) {
      onCopied();
    } else {
      onCopyFailed();
    }
  }

  private copyToClipboardFallback(value: string): boolean {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();

    let copied = false;
    try {
      copied = document.execCommand('copy');
    } catch {
      copied = false;
    } finally {
      document.body.removeChild(textarea);
    }

    return copied;
  }

  private showCreatedKeyDialog(created: IUserApiKeyCreated): void {
    this.dialogService.openDialog({
      title: 'API Key Created',
      content:
        'Copy this key now. You will not be able to see the full secret again.\n\n' + created.key,
      canClose: false,
      buttons: [
        {
          text: 'Copy Key',
          color: ButtonColor.BLUE,
          size: ButtonSize.SMALL,
          onClick: (dialog: DialogComponent) => {
            this.copyToClipboard(created.key, dialog);
          },
        },
        {
          text: 'Done',
          color: ButtonColor.ORANGE,
          size: ButtonSize.SMALL,
          actAsClose: true,
          isCloseButton: true,
        },
      ],
    });
  }

  private showSuccess(message: string, duration = 4000): void {
    this.snackbarService.openSnackbar({
      data: { message, type: 'success' },
      duration,
    });
  }

  private showError(error: unknown, fallback: string): void {
    this.snackbarService.openSnackbar({
      data: { message: getApiErrorMessage(error, fallback), type: 'error' },
      duration: 6000,
    });
  }
}
